import { useState } from 'react';

import { useHistory } from 'react-router';
import { Landing, LandingCurrentStep } from './landing/landing';
import { kebabCase } from 'es-toolkit';
import { LeaseSigningFlowStepNames, LeaseStatus } from 'models/enums';
import { StepsLayout } from 'lease-signing-flow-v2/components/steps-layout/steps-layout';
import Page404 from 'pages/Page404/Page404';
import FullPageSpinner from 'components/FullPageSpinner/FullPageSpinner';
import RenterAccountLease from 'models/renterAccounts/RenterAccountLease';
import { useLeaseSigningFlow } from './useLeaseSigningFlow';
import { getSidebarTip } from './leaseSigningUtils';
import { PATHS } from '@belong/common';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

export function LeaseSigningFlowV2() {
  const dispatch = useAppDispatch();
  const history = useHistory();

  // Use the custom hook for data fetching and state management
  const {
    leaseSigningData,
    home,
    leaseUniqueId,
    currentStep,
    steps,
    isLoading,
    employee,
    error,
    isRenewal,
    isCosigner,
    lease,
  } = useLeaseSigningFlow();

  const [isStepLoading, setIsStepLoading] = useState(false);
  const [sidebarTip, setSidebarTip] = useState(null);
  const handleNext = (stepIndex: number) => async (formData?: unknown) => {
    try {
      const flowId = leaseSigningData?.id;
      // This is only ever called from the JSX below, which renders after the
      // `!currentStep` guard — and `currentStep` is set only once the flow's
      // `steps` have loaded, so both are present by the time this runs.
      const currentStepMeta = leaseSigningData!.steps!.find(
        (step) => step.stepName === currentStep!.stepName,
      );
      const stepId = currentStepMeta?.id;

      const nextStep = steps.find((step) => step.index === stepIndex + 1);

      if (flowId && stepId) {
        // `stepId` is `currentStepMeta.id`, which the model declares required —
        // so a truthy `stepId` means the step was found, and its `stepName`
        // matched `currentStep`'s above.
        const stepSlug = kebabCase(currentStepMeta!.stepName!);

        await dispatch({
          promise: ({ client }: { client: any }) =>
            formData !== undefined
              ? client.put(
                  `/residents/lease-signing/${flowId}/${stepSlug}/${stepId}`,
                  formData,
                )
              : client.put(
                  `/residents/lease-signing/${flowId}/${stepSlug}/${stepId}`,
                ),
          auth: true,
        });

        if (nextStep) {
          window.location.href = `${window.location.origin}${PATHS.LEASE_SIGNING_FLOW_V2}/${nextStep.stepName}`;
          return;
        }

        // Fetch lease data to check isExecuted status
        const homeUniqueId = leaseSigningData?.configuration?.homeUniqueId;
        let executedRealTime = false;

        if (homeUniqueId && leaseUniqueId) {
          const unitDetail = await dispatch({
            promise: ({ client }: { client: any }) =>
              client.get(
                `/residents/units/${homeUniqueId}?leaseId=${leaseUniqueId}`,
              ),
            auth: true,
          });

          executedRealTime =
            unitDetail?.leaseDetail?.basicInfo?.status === LeaseStatus.Executed;
        }

        if (!isRenewal && executedRealTime) {
          // missing condition is to check isExecuted (ask back end team)
          history.push(PATHS.SIGNED_LEASE_NEW_SUCCESS);
          return;
        }
        // old success page for renewals and !isExecuted (not sure about this)

        if (homeUniqueId && leaseUniqueId) {
          // Old success needs homeId and leaseId
          history.push(
            PATHS.SIGNED_LEASE_OLD_SUCCESS.replace(
              ':homeId',
              homeUniqueId,
            ).replace(':leaseId', leaseUniqueId),
          );
        }
      }
    } catch (error) {
      console.error('Error updating step:', error);
      const leaseAlreadySignedError = Array.isArray(error)
        ? error.find((e) => e?.code === 'LEASE_ALREADY_SIGNED')
        : undefined;
      history.push(
        `${PATHS.RESIDENTS_ACCOUNT_TOURS_AND_APPS}?error=${encodeURIComponent(
          leaseAlreadySignedError?.message ??
            'There was an error processing your request. Please try again or contact support.',
        )}`,
      );
    }
  };

  if (error) {
    console.error('Global Error:', error);
    history.push(
      `${PATHS.RESIDENTS_ACCOUNT_TOURS_AND_APPS}?error=${encodeURIComponent(
        'There was an error with your lease. Please try again or contact support.',
      )}`,
    );
    return <Page404 />;
  }

  if (isLoading || !currentStep) {
    return <FullPageSpinner />;
  }

  return (
    <>
      {currentStep &&
        currentStep.stepName === LeaseSigningFlowStepNames.GetStarted && (
          <Landing
            // The GetStarted step's payload is what `LandingCurrentStep`
            // describes, but the merged step type cannot say so: the payload half
            // arrives through `dispatch` untyped, so it is an index signature —
            // which a spread drops, leaving nothing for the two types to overlap
            // on. Hence `unknown` rather than a direct assertion.
            currentStep={
              {
                ...currentStep,
                isCosigner:
                  isCosigner !== undefined
                    ? isCosigner
                    : currentStep.isCosigner,
              } as unknown as LandingCurrentStep
            }
            onStartSigning={() => {
              handleNext(0)();
            }}
          />
        )}

      {currentStep &&
        currentStep.stepName !== LeaseSigningFlowStepNames.GetStarted && (
          <StepsLayout
            currentStep={currentStep}
            employeeAssignment={employee}
            home={home}
            isFlowLoading={isLoading || isStepLoading}
            lease={lease as unknown as RenterAccountLease}
            setLoading={setIsStepLoading}
            setSidebarTip={setSidebarTip}
            showEmployeeTip={
              currentStep?.name === LeaseSigningFlowStepNames.SignLease
            }
            sidebarProps={{
              currentStep,
            }}
            onSubmit={handleNext(currentStep.index)}
            steps={steps}
            tip={getSidebarTip(currentStep, sidebarTip, home, lease)}
          />
        )}
    </>
  );
}
